import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function FeeMaster() {
  const [feeConfigs, setFeeConfigs] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currencies] = useState(["USD", "INR", "EUR", "AED", "THB", "KRW"]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);

  const [formData, setFormData] = useState({
    visa_type_id: "",
    currency: "INR",
    visa_fee: "0",
    service_fee: "0",
    vat_percentage: "0",
    handling_fee: "0",
    is_active: true,
  });

  // ------------------------------------------------------
  // LOAD DATA
  // ------------------------------------------------------
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [visaRes, feeRes] = await Promise.all([
        api.adminGetVisaTypes(),
        axios.get(`${API}/admin/fee-configs`, { headers: getAdminAuthHeader() }),
      ]);

      setVisaTypes(Array.isArray(visaRes.data) ? visaRes.data : []);
      setFeeConfigs(Array.isArray(feeRes.data) ? feeRes.data : []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------
  // RESET FORM FOR NEW CONFIG
  // ------------------------------------------------------
  const resetForm = () => {
    setFormData({
      visa_type_id: "",
      currency: "INR",
      visa_fee: "0",
      service_fee: "0",
      vat_percentage: "0",
      handling_fee: "0",
      is_active: true,
    });
  };

  // ------------------------------------------------------
  // TOTAL CALCULATION
  // ------------------------------------------------------
  const calculateTotal = () => {
    const visa = parseFloat(formData.visa_fee) || 0;
    const service = parseFloat(formData.service_fee) || 0;
    const handling = parseFloat(formData.handling_fee) || 0;
    const vatPercent = parseFloat(formData.vat_percentage) || 0;

    const subtotal = visa + service + handling;
    const vat = subtotal * (vatPercent / 100);

    return (subtotal + vat).toFixed(2);
  };

  // ------------------------------------------------------
  // CREATE / UPDATE
  // ------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      visa_fee: parseFloat(formData.visa_fee),
      service_fee: parseFloat(formData.service_fee),
      handling_fee: parseFloat(formData.handling_fee),
      vat_percentage: parseFloat(formData.vat_percentage),
    };

    try {
      if (editingFee) {
        await axios.put(`${API}/admin/fee-configs/${editingFee.id}`, payload, {
          headers: getAdminAuthHeader(),
        });
        toast.success("Fee configuration updated");
      } else {
        await axios.post(`${API}/admin/fee-configs`, payload, {
          headers: getAdminAuthHeader(),
        });
        toast.success("Fee configuration created");
      }

      setDialogOpen(false);
      setEditingFee(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  // ------------------------------------------------------
  // DELETE
  // ------------------------------------------------------
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this fee configuration?")) return;

    try {
      await axios.delete(`${API}/admin/fee-configs/${id}`, {
        headers: getAdminAuthHeader(),
      });
      toast.success("Fee configuration deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ------------------------------------------------------
  // EDIT MODE → CLEAN FIELDS
  // ------------------------------------------------------
  const openEditDialog = (fee) => {
    setEditingFee(fee);

    setFormData({
      visa_type_id: String(fee.visa_type_id || ""),
      currency: fee.currency || "INR",
      visa_fee: String(fee.visa_fee),
      service_fee: String(fee.service_fee),
      handling_fee: String(fee.handling_fee),
      vat_percentage: String(fee.vat_percentage),
      is_active: !!fee.is_active,
    });

    setDialogOpen(true);
  };

  // ------------------------------------------------------
  // RENDER
  // ------------------------------------------------------
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64 text-gray-500">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Fee Master Configuration
          </h2>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  setEditingFee(null);
                  resetForm();
                }}
              >
                Add Fee Configuration
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingFee ? "Edit" : "Add"} Fee Configuration
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* VISA TYPE */}
                  <div>
                    <Label>Visa Type *</Label>
                    <Select
                      value={formData.visa_type_id}
                      onValueChange={(value) =>
                        setFormData({ ...formData, visa_type_id: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select visa type" />
                      </SelectTrigger>
                      <SelectContent>
                        {visaTypes.map((v) => (
                          <SelectItem key={v.id} value={String(v.id)}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* CURRENCY */}
                  <div>
                    <Label>Currency *</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) =>
                        setFormData({ ...formData, currency: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* FEES SECTION */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Fee Breakdown</h4>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Visa Fee */}
                    <div>
                      <Label>Visa Fee *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.visa_fee}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            visa_fee: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Service Fee */}
                    <div>
                      <Label>Service Fee *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.service_fee}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            service_fee: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Handling Fee */}
                    <div>
                      <Label>Handling Fee *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.handling_fee}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            handling_fee: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* VAT */}
                    <div>
                      <Label>VAT (%) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.vat_percentage}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            vat_percentage: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">
                        Total Amount:
                      </span>
                      <span className="text-xl font-bold text-blue-600">
                        {formData.currency} {calculateTotal()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ACTIVE TOGGLE */}
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label>Active Configuration</Label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {editingFee ? "Update" : "Create"} Fee Configuration
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {feeConfigs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Visa Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Currency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Visa Fee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Service Fee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Handling
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      VAT %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {feeConfigs.map((fee) => {
                    const subtotal =
                      fee.visa_fee + fee.service_fee + fee.handling_fee;
                    const vat = subtotal * (fee.vat_percentage / 100);
                    const total = subtotal + vat;

                    return (
                      <tr key={fee.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {fee.visa_type?.name || "Unknown"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {fee.currency}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {fee.visa_fee}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {fee.service_fee}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {fee.handling_fee}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {fee.vat_percentage}%
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          {fee.currency} {total.toFixed(2)}
                        </td>

                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              fee.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {fee.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-sm space-x-2">
                          <Button
                            onClick={() => openEditDialog(fee)}
                            variant="outline"
                            size="sm"
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDelete(fee.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              No fee configurations found. Add one to get started.
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}